import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Search, Edit, Trash2, Eye, Calendar } from 'lucide-react';
import { EditProjectForm } from './EditProjectForm';
import { useToast } from '@/hooks/use-toast';

interface ProjectForManager {
  id: string;
  title: string;
  description: string;
  location: string;
  status: string;
  featured: boolean;
  created_at: string;
  project_images: { 
    id: string;
    image_url: string; 
    alt_text: string;
    caption: string;
    sort_order: number;
  }[];
  project_category_assignments: { 
    project_categories: { id: string; name: string; slug: string } 
  }[];
}

export const ProjectsManager = () => {
  const [projects, setProjects] = useState<ProjectForManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState<ProjectForManager | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          project_images(id, image_url, alt_text, caption, sort_order),
          project_category_assignments(
            project_categories(id, name, slug)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast({
        title: "Error",
        description: "Failed to load projects",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project deleted successfully",
      });

      loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive",
      });
    }
  };

  const toggleProjectStatus = async (projectId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';

    try {
      const { error } = await supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', projectId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Project ${newStatus === 'published' ? 'published' : 'moved to draft'}`,
      });

      loadProjects();
    } catch (error) {
      console.error('Error updating project status:', error);
      toast({
        title: "Error",
        description: "Failed to update project status",
        variant: "destructive",
      });
    }
  };

  const filteredProjects = projects.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="flex items-center space-x-2 w-full sm:w-auto">
        <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <Input
          placeholder="Search projects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 sm:max-w-sm min-h-[44px]"
        />
      </div>

      {/* Projects List */}
      {filteredProjects.length === 0 ? (
        <Alert>
          <AlertDescription>
            {searchTerm ? 'No projects match your search.' : 'No projects found. Create your first project!'}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="grid gap-6">
          {filteredProjects.map((project) => (
            <Card key={project.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <CardTitle className="text-lg">{project.title}</CardTitle>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(project.created_at).toLocaleDateString()}</span>
                      {project.location && (
                        <>
                          <span>•</span>
                          <span>{project.location}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={project.status === 'published' ? 'default' : 'secondary'}>
                      {project.status}
                    </Badge>
                    {project.featured && (
                      <Badge variant="outline">Featured</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {project.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {project.description}
                    </p>
                  )}

                  {/* Categories */}
                  {project.project_category_assignments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {project.project_category_assignments.map((assignment, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {assignment.project_categories.name}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Images preview */}
                  {project.project_images.length > 0 && (
                    <div className="flex space-x-2 overflow-x-auto pb-2">
                      {project.project_images.slice(0, 3).map((image, index) => (
                        <img
                          key={index}
                          src={image.image_url}
                          alt={image.alt_text || project.title}
                          className="h-16 w-16 sm:h-20 sm:w-20 rounded object-cover flex-shrink-0"
                        />
                      ))}
                      {project.project_images.length > 3 && (
                        <div className="h-16 w-16 sm:h-20 sm:w-20 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground flex-shrink-0">
                          +{project.project_images.length - 3}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between pt-2 border-t gap-3 sm:gap-0">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedProject(project);
                          setIsEditDialogOpen(true);
                        }}
                        className="min-h-[44px] justify-center"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        variant={project.status === 'published' ? 'secondary' : 'default'}
                        onClick={() => toggleProjectStatus(project.id, project.status)}
                        className="min-h-[44px] justify-center"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">{project.status === 'published' ? 'Unpublish' : 'Publish'}</span>
                        <span className="sm:hidden">{project.status === 'published' ? 'Hide' : 'Show'}</span>
                      </Button>
                    </div>

                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteProject(project.id)}
                      className="min-h-[44px] justify-center sm:w-auto"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog - Outside the loop */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) setSelectedProject(null);
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto m-2 sm:m-6">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <EditProjectForm
              project={{
                ...selectedProject,
                status: selectedProject.status as 'draft' | 'published',
                project_images: selectedProject.project_images.map(img => ({
                  id: img.id,
                  image_url: img.image_url,
                  alt_text: img.alt_text,
                  caption: img.caption,
                  sort_order: img.sort_order
                })),
                project_category_assignments: selectedProject.project_category_assignments.map(assignment => ({
                  project_categories: { 
                    id: assignment.project_categories.id,
                    name: assignment.project_categories.name 
                  }
                }))
              }}
              onSuccess={() => {
                setIsEditDialogOpen(false);
                setSelectedProject(null);
                loadProjects();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};